import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody } from './ui/dialog';
import { ApiService } from '../utils/api';
import { useToast } from './Toast';
import { BarChart3, RefreshCcw, Plus } from 'lucide-react';

const ScanStatsManager = ({ eventId, eventName, triggerClassName = 'flex-1 sm:flex-none', size = 'sm' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPurpose, setSelectedPurpose] = useState('');
  const [increment, setIncrement] = useState('');
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const { data: purposes = [], isLoading: purposesLoading } = useQuery({
    queryKey: ['qrScanPurposes', eventId],
    queryFn: () => ApiService.getQRScanPurposes(eventId),
    enabled: isOpen && !!eventId,
  });

  useEffect(() => {
    if (!selectedPurpose && purposes.length > 0) {
      setSelectedPurpose(purposes[0].purpose);
    }
  }, [purposes, selectedPurpose]);

  const statsKey = ['qrScanStats', eventId, selectedPurpose];
  const { data: stats, isFetching: statsLoading } = useQuery({
    queryKey: statsKey,
    queryFn: () => ApiService.getQRScanPurposeStats(eventId, selectedPurpose),
    enabled: isOpen && !!selectedPurpose,
  });

  const onStatsUpdated = (message) => (data) => {
    queryClient.setQueryData(statsKey, data);
    showSuccess(message);
  };

  const syncMutation = useMutation({
    mutationFn: () => ApiService.syncQRScanPurposeStats(eventId, selectedPurpose),
    onSuccess: onStatsUpdated('Scan stats synced with registrations'),
    onError: (error) => showError(error.message || 'Failed to sync scan stats'),
  });

  const adjustMutation = useMutation({
    mutationFn: (value) => ApiService.adjustQRScanPurposeStats(eventId, selectedPurpose, value),
    onSuccess: (data) => {
      setIncrement('');
      onStatsUpdated('Scan stats updated')(data);
    },
    onError: (error) => showError(error.message || 'Failed to update scan stats'),
  });

  const parsedIncrement = Number.parseInt(increment, 10);
  const canAdjust = Number.isInteger(parsedIncrement) && parsedIncrement !== 0;
  const isBusy = syncMutation.isPending || adjustMutation.isPending;

  return (
    <>
      <Button variant="outline" size={size} onClick={() => setIsOpen(true)} className={triggerClassName}>
        <BarChart3 className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
        <span className="text-xs sm:text-sm">Scan Stats</span>
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Scan Stats{eventName ? ` – ${eventName}` : ''}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <div className="space-y-5">
              <div className="space-y-2">
                <Label>Scan purpose</Label>
                <Select value={selectedPurpose || undefined} onValueChange={setSelectedPurpose}>
                  <SelectTrigger>
                    <SelectValue placeholder={purposesLoading ? 'Loading…' : 'Select a scan purpose'} />
                  </SelectTrigger>
                  <SelectContent>
                    {purposes.map((p) => (
                      <SelectItem key={p.purpose} value={p.purpose}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between rounded-lg border bg-gray-50 p-4">
                <span className="text-sm text-gray-600">Total scans</span>
                <span className="text-2xl font-bold text-gray-900">
                  {statsLoading && !stats ? '…' : stats?.totalScans ?? 0}
                </span>
              </div>

              <Button
                variant="outline"
                className="w-full"
                disabled={!selectedPurpose || isBusy}
                onClick={() => syncMutation.mutate()}
              >
                <RefreshCcw className={`w-4 h-4 mr-2 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
                Sync stats from registrations
              </Button>

              <div className="space-y-2">
                <Label htmlFor="scan-stats-increment">Add scans manually</Label>
                <div className="flex gap-2">
                  <Input
                    id="scan-stats-increment"
                    type="number"
                    step={1}
                    placeholder="e.g. 50 (negative to reduce)"
                    value={increment}
                    onChange={(e) => setIncrement(e.target.value)}
                  />
                  <Button
                    disabled={!selectedPurpose || !canAdjust || isBusy}
                    onClick={() => adjustMutation.mutate(parsedIncrement)}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Update
                  </Button>
                </div>
              </div>
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ScanStatsManager;
